import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { BLOG_POSTS, BLOG_CATEGORIES } from '../data/blogPosts';
import { AR_BLOG_SLUGS } from '../i18nRoutes';
import { translations } from '../translations';

export default function BlogPage({ language, setLanguage }) {
  const t = translations[language];
  const isAr = language === 'ar';
  const [activeCat, setActiveCat] = useState('all');

  const filtered = activeCat === 'all'
    ? BLOG_POSTS
    : BLOG_POSTS.filter(p => p.category === activeCat);

  const postLink = (post) => (isAr && AR_BLOG_SLUGS.includes(post.slug)) ? `/ar/blog/${post.slug}` : `/blog/${post.slug}`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Viaturkey Travel Blog",
    "url": isAr ? "https://viaturkey.net/ar/blog" : "https://viaturkey.net/blog",
    "inLanguage": isAr ? "ar" : "en",
    "description": "Expert Istanbul travel guides, tour tips, shore excursion planning, and Turkish citizenship investment advice.",
    "publisher": { "@type": "Organization", "name": "Viaturkey", "url": "https://viaturkey.net" }
  };

  return (
    <div className="blog-page" dir={isAr ? 'rtl' : 'ltr'} data-testid="blog-page">
      <SEOHead
        title={isAr ? (t.seo?.blogTitle || 'Istanbul Travel Blog | Viaturkey') : 'Istanbul Travel Blog | Viaturkey'}
        description={isAr ? (t.seo?.blogDesc || '') : 'Expert Istanbul travel guides, private tour tips, Galataport shore excursion planning, and Turkish citizenship investment advice for Gulf Arab travelers.'}
        canonical="https://viaturkey.net/blog"
        schema={schema}
      />
      <Header language={language} t={t} />

      <section className="blog-hero" data-testid="blog-hero">
        <div className="blog-hero-content">
          <h1>{isAr ? (t.seo?.blogH1 || 'مدونة السفر إلى إسطنبول') : 'Istanbul Travel Blog'}</h1>
          <p>{isAr ? (t.seo?.blogSub || '') : 'Expert guides, travel tips, and insider knowledge for your Istanbul journey'}</p>
        </div>
      </section>

      <section className="blog-categories" data-testid="blog-categories">
        {BLOG_CATEGORIES.map(cat => (
          <button
            key={cat.key}
            className={`blog-cat-btn ${activeCat === cat.key ? 'active' : ''}`}
            onClick={() => setActiveCat(cat.key)}
            data-testid={`blog-cat-${cat.key}`}
          >{isAr ? (cat.labelAr || cat.label) : cat.label}</button>
        ))}
      </section>

      <section className="blog-grid" data-testid="blog-grid">
        {filtered.map(post => (
          <article key={post.slug} className="blog-card" data-testid={`blog-card-${post.slug}`}>
            <Link to={postLink(post)}>
              <img src={post.thumbnail} alt={isAr ? (post.titleAr || post.title) : post.title} loading="lazy" className="blog-card-img" />
            </Link>
            <div className="blog-card-body">
              <Link to={`/blog/category/${post.category}`} className="blog-card-cat">{isAr ? (BLOG_CATEGORIES.find(c => c.key === post.category)?.labelAr || post.category) : (BLOG_CATEGORIES.find(c => c.key === post.category)?.label)}</Link>
              <h2 className="blog-card-title"><Link to={postLink(post)}>{isAr ? (post.titleAr || post.title) : post.title}</Link></h2>
              <p className="blog-card-excerpt">{isAr ? (post.excerptAr || post.excerpt) : post.excerpt}</p>
              <div className="blog-card-meta">
                <time>{new Date(post.date).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</time>
                <span>{isAr ? post.readTime.replace(' min', ' دقائق') : `${post.readTime} read`}</span>
              </div>
              <Link to={postLink(post)} className="blog-card-link" data-testid={`read-more-${post.slug}`}>{isAr ? (t.seo?.readMore || 'اقرأ المزيد') : 'Read More'}</Link>
            </div>
          </article>
        ))}
      </section>

      <Footer t={t} />
    </div>
  );
}
